import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { t } from 'i18next';
import { FormEvent, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import RestHttpClient from '../../../common/RestHttpClient';
import { EnhancedTable } from '../../../common/table/EnhancedTable';
import { HeadCell } from '../../../common/table/EnhancedTableHead';
import { FormDropdown } from '../../../component/form/FormDropdown';
import { FormTextField } from '../../../component/form/FormTextField';
import { IOptionItem } from '../../../generated/ApiClient';
import { CustomerMergeContext } from './CustomerMergeContext';
import { mapSimilarCustomer, SimilarCustomerRow } from './SimilarCustomerRow';

const schema = z.object({
  addressCountryId: z.number().nonnegative().optional(),
  addressPostalCode: z.string().optional(),
  addressCity: z.string().optional(),
  addressStreet: z.string().optional(),
  companyName: z.string().optional(),
});

export type CommercialCustomerMergeFormValues = z.infer<typeof schema>;

interface Props {
  customerId: number;
  countries: IOptionItem[];
  headCells: readonly HeadCell<SimilarCustomerRow>[];
  isPoseidon?: boolean;
}

export function CommercialCustomerMergeSearch({
  customerId,
  countries,
  headCells,
  isPoseidon,
}: Props) {
  const {
    search: {
      selected,
      setSelected,
      setLoading,
      setPage,
      page,
      setCustomers,
      customers,
      loading,
      commercial: { searchValues, setSearchValues },
    },
  } = useContext(CustomerMergeContext);
  const { control, handleSubmit } = useForm<CommercialCustomerMergeFormValues>({
    resolver: zodResolver(schema),
    defaultValues: searchValues,
  });

  const fetchCustomers = (sv = searchValues, p = page) => {
    setLoading(true);

    //Backend unfortunately accepts country name only...
    var country;
    if (sv.addressCountryId)
      country = countries.filter((c) => c.value === sv.addressCountryId)[0].label;

    RestHttpClient.searchSimilarCommercial(
      customerId,
      sv.companyName,
      country,
      sv.addressCity,
      sv.addressStreet,
      sv.addressPostalCode,
      p,
      25,
      []
    )
      .then((res) => {
        const newCustomers = res.data;

        if (p === 0 && newCustomers.items.length) {
          setCustomers(newCustomers.items);
        } else if (newCustomers.items.length) {
          setCustomers(customers.concat(newCustomers.items));
        }
      })
      .catch((_) => {
        setCustomers([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (page > 0) {
      fetchCustomers();
    }
    // eslint-disable-next-line
  }, [page]);

  const onSubmit = (event: FormEvent) => {
    handleSubmit(async (values) => {
      setSelected([]);
      setCustomers([]);
      setPage(0);
      setSearchValues(values);
      fetchCustomers(values, 0);
    })(event);
  };

  const rows: SimilarCustomerRow[] = customers.map((c) => mapSimilarCustomer(c));

  const onSelection = (ev: React.SyntheticEvent<unknown>, names: readonly string[]) => {
    setSelected(names.slice());
  };

  const loadNewPage = () => {
    setPage(page + 1);
  };

  return (
    <Box>
      <form onSubmit={onSubmit} noValidate>
        <Box className="search-panel">
          <Grid item container spacing={4} md={12}>
            <Grid item md={4}>
              <FormTextField
                control={control}
                label={t('customers.merge.list.companyName')}
                name="companyName"
              />
            </Grid>

            <Grid item md={4}>
              <FormDropdown
                control={control}
                label={t('customers.search.addressCountry')}
                name="addressCountryId"
                options={countries}
              />
              <FormTextField
                control={control}
                label={t('customers.search.addressCity')}
                name="addressCity"
              />
            </Grid>

            <Grid item md={4}>
              <FormTextField
                control={control}
                label={t('customers.search.addressStreet')}
                name="addressStreet"
              />
              <FormTextField
                control={control}
                label={t('customers.search.addressPostalCode')}
                name="addressPostalCode"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', mt: 1 }}>
            <Button onClick={onSubmit} variant="contained" sx={{ ml: 'auto' }}>
              {t('search.start')}
            </Button>
          </Box>
        </Box>
      </form>
      <EnhancedTable
        rows={rows}
        headCells={headCells}
        hideCheckbox={false}
        variant="endless"
        loadNewPage={loadNewPage}
        loading={loading}
        enableSelection
        enableSingleSelection={isPoseidon}
        onSelection={onSelection}
        defaultSelected={selected}
      />
      {!loading && !customers.length && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Typography variant="body1">{t('search.startHint')}</Typography>
        </Box>
      )}
    </Box>
  );
}
